import React, { useMemo } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  Alert,
  AlertTitle,
  Button,
  Typography,
  Box,
} from "@mui/material";
import { format, isToday } from "date-fns";
import { CLIENT_STATUS } from "../../../../constants";
import { AccountSettingsFormValues, ClientAccountSettings } from "./interface";
import ClientActivation from "../../Widgets/ClientActivation";
import { UserPermissions } from "../../../../types/permissions";

interface EditFieldsProps {
  formValues: AccountSettingsFormValues;
  accountSettings: ClientAccountSettings;
  handleChange: (
    field: string
  ) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  userPermissions: UserPermissions | null;
  clientId: string;
  customerDetails: any;
  hasScheduledCall?: boolean;
  childName?: string;
  childLastName?: string;
  onDeactivationSuccess?: () => void;
  onClearDeactivationInfo?: () => void;
  isFutureDeactivationEdit?: boolean;
  isConfirmingScheduledDeactivation?: boolean;
  onEditFutureDeactivation?: () => void;
  onConfirmScheduledDeactivation?: () => void;
}

export const EditFields: React.FC<EditFieldsProps> = ({
  formValues,
  accountSettings,
  handleChange,
  userPermissions,
  clientId,
  customerDetails,
  hasScheduledCall,
  childName,
  childLastName,
  onDeactivationSuccess,
  onClearDeactivationInfo,
  isFutureDeactivationEdit,
  isConfirmingScheduledDeactivation,
  onEditFutureDeactivation,
  onConfirmScheduledDeactivation,
}) => {
  const { isActive, scheduledDeactivationData } = accountSettings;

  // Format the scheduled deactivation date in human-readable format
  const formattedDeactivationDate = useMemo(() => {
    if (!scheduledDeactivationData?.scheduled_deactivation_date) return null;
    try {
      const dateStr = scheduledDeactivationData.scheduled_deactivation_date;
      const [year, month, day] = dateStr.split("T")[0].split("-").map(Number);
      const dateObj = new Date(year, month - 1, day);
      return format(dateObj, "MMMM dd, yyyy");
    } catch {
      return null;
    }
  }, [scheduledDeactivationData?.scheduled_deactivation_date]);

  // Check if deactivation date is today
  const isDeactivationToday = useMemo(() => {
    if (!scheduledDeactivationData?.scheduled_deactivation_date) return false;
    try {
      const dateStr = scheduledDeactivationData.scheduled_deactivation_date;
      const [year, month, day] = dateStr.split("T")[0].split("-").map(Number);
      const dateObj = new Date(year, month - 1, day);
      return isToday(dateObj);
    } catch {
      return false;
    }
  }, [scheduledDeactivationData?.scheduled_deactivation_date]);

  const deactivationStatus = scheduledDeactivationData?.deactivation_status;
  const canDeactivate = userPermissions?.disable_client;

  return (
    <Grid container spacing={7}>
      <Grid item xs={12}>
        <TextField
          name="workflowStatus"
          label="Account status"
          select
          onChange={handleChange("workflowStatus")}
          value={formValues.workflowStatus}
          fullWidth
          disabled={!isActive}
        >
          <MenuItem disabled value="">
            Select a status
          </MenuItem>
          {Object.entries(CLIENT_STATUS).map(([value, text]) => (
            <MenuItem key={value} value={value}>
              {text}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {/* Alert for scheduled future deactivation - visible in edit mode */}
      {deactivationStatus === "processing" && formattedDeactivationDate && (
        <Grid item xs={12}>
          <Alert
            severity="warning"
            action={
              canDeactivate && (
                <Button
                  style={{ backgroundColor: "transparent", color: "inherit" }}
                  variant="text"
                  size="small"
                  onClick={
                    isDeactivationToday
                      ? onConfirmScheduledDeactivation
                      : onEditFutureDeactivation
                  }
                >
                  {isDeactivationToday ? "Confirm Deactivation" : "Edit Deactivation"}
                </Button>
              )
            }
          >
            <AlertTitle>{`Deactivation set for ${formattedDeactivationDate}`}</AlertTitle>
            Confirmation is required prior to deactivation
          </Alert>
        </Grid>
      )}

      {/* Client Activation/Deactivation Section */}
      {(userPermissions?.disable_client || userPermissions?.reactivate_client) &&
        deactivationStatus !== "processing" && (
          <Grid item xs={12}>
            <Alert
              severity={isActive ? "error" : "warning"}
              action={
                <Box minWidth={"max-content"}>
                  <ClientActivation
                    isActive={isActive}
                    hasScheduledCall={hasScheduledCall}
                    userPermissions={userPermissions}
                    clientId={clientId}
                    customerDetails={customerDetails}
                    isFutureDeactivationEdit={isFutureDeactivationEdit}
                    onDeactivationSuccess={onDeactivationSuccess}
                    onClearDeactivationInfo={onClearDeactivationInfo}
                    isConfirmingScheduledDeactivation={isConfirmingScheduledDeactivation}
                    childName={childName}
                    childLastName={childLastName}
                  />
                </Box>
              }
            >
              <AlertTitle>{`${isActive ? "Deactivate" : "Activate"} Client Account`}</AlertTitle>
              <Typography variant="body2">
                {isActive
                  ? "Deactivation will suspend account access for the client"
                  : "Reactivating the client account will return the account status to the previous state before deactivation"}
              </Typography>
            </Alert>
          </Grid>
        )}

      {/* Render ClientActivation to handle dialog for scheduled deactivation */}
      {deactivationStatus === "processing" && canDeactivate && (
        <Grid item xs={12} sx={{ display: "none" }}>
          <ClientActivation
            isActive={isActive}
            hasScheduledCall={hasScheduledCall}
            userPermissions={userPermissions}
            clientId={clientId}
            customerDetails={customerDetails}
            isFutureDeactivationEdit={isFutureDeactivationEdit}
            onDeactivationSuccess={onDeactivationSuccess}
            onClearDeactivationInfo={onClearDeactivationInfo}
            hideButton={true}
            isConfirmingScheduledDeactivation={isConfirmingScheduledDeactivation}
            childName={childName}
            childLastName={childLastName}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default EditFields;
